import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import axios from 'axios'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getCheckpoints,
  updateCheckpointSelection,
  updateMultipleSelections,
} from '@/apps/legacy/features/guidance/apis/checkpoint'
import { CheckpointItem } from '@/apps/legacy/features/guidance/types/guidanceCheckPoints'
import { useToast } from '@/shared/contexts/ToastContext'
import { HTTP_STATUS } from '@/constants/errors'
import { getErrorMessage } from '@/shared/utils/errorHandling'

// ========================================
// Error Types
// ========================================

export interface CheckpointError {
  message: string
  hasAccessError: boolean
}

// ========================================
// useCheckpointsQuery - チェックポイント取得
// ========================================

/**
 * チェックポイント取得クエリ
 *
 * @param requestId - リクエストID（空文字の場合はスキップ）
 * @returns TanStack Query result with data: CheckpointItem[], error: CheckpointError
 */
export const useCheckpointsQuery = (requestId: string) => {
  const { showToast } = useToast()

  return useQuery<CheckpointItem[], CheckpointError>({
    queryKey: queryKeys.checkpoints.detail(requestId),
    queryFn: async (): Promise<CheckpointItem[]> => {
      if (!requestId) {
        return []
      }

      try {
        const response = await getCheckpoints(requestId)
        return response.data.checkpoints
      } catch (err) {
        let errorMessage = getErrorMessage(err)
        let hasAccessError = false

        if (axios.isAxiosError(err)) {
          // 403はアクセス権限がないケース
          if (err.response?.status === HTTP_STATUS.FORBIDDEN) {
            const errorCode = (err.response.data as { code?: string })?.code
            if (errorCode === 'ACCESS_DENIED') {
              errorMessage = 'このデータへのアクセス権限がありません'
            } else {
              errorMessage = 'アクセスが拒否されました'
            }
            hasAccessError = true
          }
          // 404はYakurekiEvent自体が存在しないケース
          else if (err.response?.status === HTTP_STATUS.NOT_FOUND) {
            errorMessage = '指定されたリクエストが見つかりません'
          }
        }

        showToast(errorMessage, 'error')
        throw { message: errorMessage, hasAccessError }
      }
    },
    enabled: !!requestId,
    staleTime: 1000 * 60 * 2, // 2分キャッシュ
    retry: (failureCount, error) => {
      // アクセスエラーの場合はリトライしない
      if (error.hasAccessError) {
        return false
      }
      return failureCount < 3
    },
  })
}

// ========================================
// useUpdateCheckpointSelectionMutation
// ========================================

interface UpdateSelectionParams {
  requestId: string
  checkpointId: string
  isSelected: boolean
}

/**
 * 単一チェックポイント選択更新 Mutation
 */
export const useUpdateCheckpointSelectionMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      requestId,
      checkpointId,
      isSelected,
    }: UpdateSelectionParams) => {
      const response = await updateCheckpointSelection(
        requestId,
        checkpointId,
        isSelected
      )
      return {
        requestId,
        checkpointId,
        checkpoint: response.data.checkpoint,
      }
    },
    onSuccess: ({ requestId, checkpointId, checkpoint }) => {
      // キャッシュを楽観的に更新
      queryClient.setQueryData<CheckpointItem[]>(
        queryKeys.checkpoints.detail(requestId),
        (old) =>
          old?.map((item) =>
            item.id === checkpointId
              ? { ...item, isSelected: checkpoint.isSelected }
              : item
          )
      )
    },
    onError: () => {
      showToast('選択状態の更新に失敗しました', 'error')
    },
  })
}

// ========================================
// useUpdateMultipleSelectionsMutation
// ========================================

interface UpdateMultipleParams {
  requestId: string
  selections: Array<{ id: string; isSelected: boolean }>
}

/**
 * 複数チェックポイント選択一括更新 Mutation
 */
export const useUpdateMultipleSelectionsMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({ requestId, selections }: UpdateMultipleParams) => {
      await updateMultipleSelections(requestId, selections)
      return { requestId, selections }
    },
    onSuccess: ({ requestId, selections }) => {
      // キャッシュを楽観的に更新
      queryClient.setQueryData<CheckpointItem[]>(
        queryKeys.checkpoints.detail(requestId),
        (old) =>
          old?.map((item) => {
            const selection = selections.find((s) => s.id === item.id)
            return selection
              ? { ...item, isSelected: selection.isSelected }
              : item
          })
      )
    },
    onError: () => {
      showToast('選択状態の一括更新に失敗しました', 'error')
    },
  })
}

// ========================================
// useCheckpointSelectionActions - 選択操作ユーティリティ
// ========================================

/**
 * チェックポイント選択操作のユーティリティフック
 *
 * @param requestId - リクエストID
 * @param checkpoints - チェックポイント配列
 */
export const useCheckpointSelectionActions = (
  requestId: string,
  checkpoints: CheckpointItem[]
) => {
  const updateSelectionMutation = useUpdateCheckpointSelectionMutation()
  const updateMultipleMutation = useUpdateMultipleSelectionsMutation()

  const updateSelection = useCallback(
    async (checkpointId: string, isSelected: boolean) => {
      if (!requestId) return
      await updateSelectionMutation.mutateAsync({
        requestId,
        checkpointId,
        isSelected,
      })
    },
    [requestId, updateSelectionMutation]
  )

  const toggleSelection = useCallback(
    (checkpointId: string) => {
      const checkpoint = checkpoints.find((item) => item.id === checkpointId)
      if (checkpoint) {
        updateSelection(checkpointId, !checkpoint.isSelected)
      }
    },
    [checkpoints, updateSelection]
  )

  const selectAll = useCallback(() => {
    if (!requestId) return
    const selections = checkpoints.map((item) => ({
      id: item.id,
      isSelected: true,
    }))
    updateMultipleMutation.mutate({ requestId, selections })
  }, [requestId, checkpoints, updateMultipleMutation])

  const deselectAll = useCallback(() => {
    if (!requestId) return
    const selections = checkpoints.map((item) => ({
      id: item.id,
      isSelected: false,
    }))
    updateMultipleMutation.mutate({ requestId, selections })
  }, [requestId, checkpoints, updateMultipleMutation])

  const syncSelections = useCallback(
    (newSelectedIds: string[]) => {
      if (!requestId) return
      const selections = checkpoints.map((checkpoint) => ({
        id: checkpoint.id,
        isSelected: newSelectedIds.includes(checkpoint.id),
      }))
      updateMultipleMutation.mutate({ requestId, selections })
    },
    [requestId, checkpoints, updateMultipleMutation]
  )

  return {
    updateSelection,
    toggleSelection,
    selectAll,
    deselectAll,
    syncSelections,
    isUpdating:
      updateSelectionMutation.isPending || updateMultipleMutation.isPending,
  }
}
